import { useSport } from '@/hooks/useSport'
import { useApi } from '@/hooks/useApi'
import { BubbleZoneRow } from '@/components/BubbleZoneRow'
import { CutLine } from '@/components/CutLine'
import { Skeleton } from '@/components/ui/Skeleton'
import type { BubbleRow } from '@/types/api'

export default function BubblePage() {
  const { sport, sportLabel } = useSport()

  const { data, loading } = useApi(async (): Promise<BubbleRow[]> => {
    const params = new URLSearchParams({
      draw: '1', start: '0', length: '100',
      'order[0][column]': '0', 'order[0][dir]': 'asc',
    })
    const res = await fetch(`/api/v1/${sport}/datatables/bubble.json?${params}`, {
      headers: { Accept: 'application/json' },
    })
    const json = await res.json()
    return json.data || []
  }, [sport])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">{sportLabel} Bubble Tracker</h1>
        <p className="text-sm text-slate-400 mt-1">
          RPI ranks 25-80. The cut line at rank 48 separates likely auto-qualifiers from at-large contenders.
        </p>
      </div>

      {/* Zone legend */}
      <div className="flex flex-wrap gap-4 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-zone-safe" />
          <span className="text-slate-400">Auto-qualifier range (25-48)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-zone-bubble" />
          <span className="text-slate-400">At-large bubble (49-64)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-zone-out" />
          <span className="text-slate-400">Unlikely (65-80)</span>
        </div>
      </div>

      <div className="card p-2 sm:p-4">
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 15 }).map((_, i) => <Skeleton key={i} variant="row" />)}
          </div>
        ) : (
          <div className="space-y-0.5">
            {(data || []).map((row, i) => {
              const prevRow = i > 0 ? data![i - 1] : null
              const showCutLine = prevRow && prevRow.rank <= 48 && row.rank > 48

              return (
                <div key={row.DT_RowId}>
                  {showCutLine && <CutLine />}
                  <BubbleZoneRow
                    rank={row.rank}
                    schoolName={row.school_name}
                    conferenceName={row.conference_name}
                    recordOverall={row.record_overall}
                    recordConf={row.record_conf}
                    prevRank={row.prev_rank}
                    logoUrl={row.logo_url}
                    slug={row.slug}
                    sport={sport}
                  />
                </div>
              )
            })}
            {!data?.length && (
              <p className="text-sm text-slate-500 p-4">No bubble data available. Run the RPI ingestion job first.</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
